/**
 * Consent, as Google Consent Mode v2 wants to hear it and as a visitor gave it.
 *
 * Two things have to agree. The browser remembers an answer so the banner is
 * asked once; the container is told that answer in its own vocabulary, before
 * any tag fires. The layout's inline script owns the *default* — everything
 * denied — and this file owns every change to it.
 *
 * Functional and security storage stay granted: they cover what the site needs
 * to work at all, which is not something a banner asks permission for.
 */

export type Consent = {
    analytics: boolean
    marketing: boolean
}

export const CONSENT_STORAGE_KEY = 'kd-consent-v1'

export const CONSENT_EVENT = 'kd:consent-open'

export const ACCEPT_ALL: Consent = { analytics: true, marketing: true }

export const NECESSARY_ONLY: Consent = { analytics: false, marketing: false }

/**
 * The answer this browser gave, or nothing when it has not been asked.
 *
 * Anything unreadable counts as unasked. Private browsing throws on
 * `localStorage`, an older version of this key parses into something that is
 * not a consent, and both are better answered by asking again than by guessing
 * on the visitor's behalf.
 */
export function readConsent(): Consent | null {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY)

        if (stored === null) {
            return null
        }

        const parsed: unknown = JSON.parse(stored)

        if (typeof parsed !== 'object' || parsed === null) {
            return null
        }

        const { analytics, marketing } = parsed as Record<string, unknown>

        if (typeof analytics !== 'boolean' || typeof marketing !== 'boolean') {
            return null
        }

        return { analytics, marketing }
    } catch {
        return null
    }
}

/** Remember the answer. A browser that refuses to remember is not an error. */
export function storeConsent(consent: Consent): void {
    try {
        window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent))
    } catch {
        // Private browsing, or a full quota. The consent still applies to this
        // page view; the banner simply asks again next time.
    }
}

/**
 * Tell the container. Google's four storage types, from this site's two
 * questions: statistics is analytics storage, marketing is the three
 * advertising ones.
 */
export function applyConsent(consent: Consent): void {
    if (typeof window === 'undefined' || window.gtag === undefined) {
        return
    }

    window.gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
    })
}
